import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { DeliveryForecastDespatchAdviceItemBatch } from '../../../../../data/models'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../utils/helper/validateFormikFields'
import { useConfigContext } from '../../../../ui-config'
import { UsedBatches } from '../../../utils'
import { ConfirmationBatchesTableView } from './ConfirmationBatchesTable.view'

export type FormValues = {
  confirmationBatches?: DeliveryForecastDespatchAdviceItemBatch[]
}
export type FormFieldName = keyof DeliveryForecastDespatchAdviceItemBatch

type Props = {
  batches?: DeliveryForecastDespatchAdviceItemBatch[]
  usedBatches: UsedBatches
  isLoading: boolean
  hasLoadingRowId: (rowId: string) => boolean
  isBulkDeleteRunning: boolean
  handleCreate: (
    batch: DeliveryForecastDespatchAdviceItemBatch,
  ) => Promise<void>
  handleSave: (
    formData: DeliveryForecastDespatchAdviceItemBatch,
  ) => Promise<void>
  handleDelete: (
    batches: DeliveryForecastDespatchAdviceItemBatch[],
    callback?: () => void,
  ) => Promise<void>
}

export const ConfirmationBatchesTableForm: React.FC<Props> = ({
  batches,
  usedBatches,
  isLoading,
  hasLoadingRowId,
  isBulkDeleteRunning,
  handleCreate,
  handleSave,
  handleDelete,
}) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    validateOnChange: true,
    initialValues: {
      confirmationBatches: batches, //batches,
    },
    validate: formData => {
      let errors: any = []

      const batchNumbers =
        formData.confirmationBatches?.map(batch => batch.Batchnumber) || []
      const duplicates = batchNumbers.filter(
        (item, index) => batchNumbers.indexOf(item) !== index,
      )

      formData.confirmationBatches?.forEach((data, index) => {
        if (!errors[index]) {
          errors[index] = {}
        }

        const fieldsToValidate: FieldToValidate[] = [
          {
            fieldName: 'Batchnumber',
            config:
              config.deliveryForecastDespatchAdvice.addConfirmationBatch
                .batchnumber,
          },
          {
            fieldName: 'Quantity',
            config:
              config.deliveryForecastDespatchAdvice.addConfirmationBatch
                .quantity,
          },
        ]

        validateFormikFields(fieldsToValidate, data, errors[index])

        if (!data.Batchnumber) {
          errors[index] = {
            ...errors[index],
            Batchnumber: t(
              'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.columns.batchnumber.errors.required',
            ),
          }
        } else if (duplicates?.includes(data.Batchnumber)) {
          errors[index] = {
            ...errors[index],
            Batchnumber: t(
              'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.columns.batchnumber.errors.duplicate',
            ),
          }
        }

        if (!data.Quantity === null || !data.Quantity === undefined) {
          errors[index] = {
            ...errors[index],
            Quantity: t(
              'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.columns.quantity.errors.required',
            ),
          }
        } else if (data.Quantity < 1) {
          errors[index] = {
            ...errors[index],
            Quantity: t(
              'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.columns.quantity.errors.numberTooSmall',
              {
                number: 0,
              },
            ),
          }
        } else if (data.Quantity < usedBatches[data.Batchnumber]) {
          errors[index] = {
            ...errors[index],
            Quantity: t(
              'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.columns.quantity.errors.batchQuantityTooLow',
              {
                number: usedBatches[data.Batchnumber],
              },
            ),
          }
        }
      })

      return errors
    },
    onSubmit: async formData => {},
  })

  const isFormFieldValid = (index: number, name: FormFieldName) =>
    !(
      Array.isArray(formik.touched.confirmationBatches) &&
      formik.touched.confirmationBatches[index]?.[name] &&
      Array.isArray(formik.errors) &&
      (
        formik.errors[
          index
        ] as FormikErrors<DeliveryForecastDespatchAdviceItemBatch>
      )?.[name]
    )

  const getFormErrorMessage = (index: number, name: FormFieldName) => {
    // @ts-ignore
    const fieldErrors = formik.errors[index]

    return (
      !isFormFieldValid(index, name) && (
        <small className="p-error">{fieldErrors?.[name]}</small>
      )
    )
  }

  return (
    <ConfirmationBatchesTableView
      formik={formik}
      batches={batches}
      isLoading={isLoading}
      hasLoadingRowId={hasLoadingRowId}
      isBulkDeleteRunning={isBulkDeleteRunning}
      handleCreate={handleCreate}
      handleSave={handleSave}
      handleDelete={handleDelete}
      isFormFieldValid={isFormFieldValid}
      getFormErrorMessage={getFormErrorMessage}
    />
  )
}
